/* eslint-disable import/prefer-default-export */
/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Col } from "react-bootstrap"

import { STORAGE_AWS, STORAGE_GOOGLE, STORAGE_MICROSOFT } from "const"
import { MtDriveFile } from "server/models"
import { deepCopy } from "client/utils"
import {
    StoreType,
    UploadFile,
    addModal,
    getFile,
    getFileLogo,
    createFileName,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    CKEditor,
    Button,
    PaperclipIcon,
    AmazonIcon,
    GoogleIcon,
    OneDriveIcon,
    CrossIcon,
    AddToTextIcon,
    AddedToTextIcon,
} from "client/components"

import Drive from "client/pages/drive"

import {
    AttachmentManagerWrapper,
    AttachmentManagerButton,
    AttachmentManagerButtonSubtext,

    AttachmentBlockWrapper,
    AttachmentListWrapper,
    AttachmentWrapper,
    AttachmentLogo,
    AttachmentTitle,
    DeleteAttachment,
    AddToTextAttachment,
    TooltipAlignType,
    Tooltip,
} from "./styled"


let ckEditor: any = null

// inserts attached file into CKEditor as image or link
const insertIntoText = (file: MtDriveFile) => {
    const version = (file.versions && file.versions[0]) || null
    if (!version) return

    ckEditor.model.change(() => {
        const insertPosition = ckEditor.model.document.selection.getFirstPosition()

        const links = getFile(file, file.versions[0])
        let insertData: string

        if (version.type.indexOf("image") >= 0) {
            insertData = `<img src="${links.preview}" />`
        } else {
            insertData = `
                <a href="${links.download}">
                    <span style="color:hsl(240, 75%, 60%);">
                        ${createFileName(file)}
                    </span>
                </a>
            `
        }

        const viewFragment = ckEditor.data.processor.toView(insertData)
        const modelFragment = ckEditor.data.toModel(viewFragment)
        ckEditor.model.insertContent(modelFragment, insertPosition)
    })
}

// removes attached file from CKEditor
const deleteFromText = (file: MtDriveFile) => {
    const version = (file.versions && file.versions[0]) || null
    if (!version) return

    let stopLoop = false
    const findAndRemoveNested = (parent: any, href: string) => {
        if (!parent.getChildren) return
        const children: any = parent.getChildren()
        for (const child of children) {
            if (stopLoop) break
            if (child.getAttribute("linkHref") === href || child.getAttribute("src") === href) {
                ckEditor.model.change((writer: any) => writer.remove(child))
                stopLoop = true
            }
            findAndRemoveNested(child, href)
        }
    }

    const urls = getFile(file, file.versions[0])
    ckEditor.model.change(() => {
        const root: any = ckEditor.model.document.getRoot()
        findAndRemoveNested(root, urls.preview)
        findAndRemoveNested(root, urls.download)
    })
}

// requiered for correct upate after upload via CKEditor. It cannot see changes of property value
let attachmentsCopy: MtDriveFile[] = []

interface EditProps {
    description: string
    setDescription: (data: string) => void
    attachments: readonly MtDriveFile[]
    setAttachments: (data: MtDriveFile[]) => void
    showTitle?: boolean
}

export const CKEditorAttachmentsEdit = ({
    description,
    setDescription,
    attachments,
    setAttachments,
    showTitle = true,
}: EditProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [tooltipAlign, setTooltipAlign] = useState<TooltipAlignType>(null)
    const [showAttachments, setShowAttachments] = useState(false)

    const { defaultStorage } = accountSettings.drive

    useEffect(() => {
        attachmentsCopy = deepCopy(attachments)
    }, [attachments])

    const defineTooltipAlign = (e: React.MouseEvent) => {
        const pageForm = e.currentTarget.closest(PageForm)
        const { left } = pageForm.getBoundingClientRect()

        if (e.clientX < left + 100) setTooltipAlign("left")
        else if (window.innerWidth - e.clientX < 150) setTooltipAlign("right")
        else setTooltipAlign("center")
    }

    const uploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const promiseArray: Promise<any>[] = []
        for (let i = 0; i < e.target.files.length; i++) {
            const file = e.target.files[i]
            promiseArray.push(
                UploadFile({
                    file,
                    storage: defaultStorage,
                    userData,
                    projectId: project.id,
                    targetFolderId: null,
                    callbackSuccess: (newFile) => addAttachment(newFile),
                }),
            )
        }
        Promise.all(promiseArray)
    }

    const addAttachment = (file: MtDriveFile) => {
        attachmentsCopy.push(file)
        setAttachments(attachmentsCopy)
    }

    const addAttachments = (files: readonly MtDriveFile[]) => {
        files.forEach((file) => {
            const existing = attachmentsCopy.find((row) => (row.id === file.id))
            if (!existing) attachmentsCopy.push(file)
        })
        setAttachments(attachmentsCopy)
    }

    const deleteAttachment = (file: MtDriveFile) => {
        deleteFromText(file)
        const attachmentsFiltered = attachments.filter((row) => (row.id !== file.id))
        setAttachments(attachmentsFiltered)
    }

    return (
        <FormGroup>
            { showTitle && <Label>{lang.description}</Label> }
            <CKEditor
                data={description}
                useImagePlugin
                onInit={(editor) => {
                    ckEditor = editor
                }}
                onUpload={addAttachment}
                onChange={setDescription}
            />

            <AttachmentManagerWrapper>
                <AttachmentManagerButton
                    show={showAttachments}
                    onClick={() => setShowAttachments(!showAttachments)}
                >
                    <PaperclipIcon />
                    {lang.file}
                    {
                        attachments.length > 0 && (
                            <AttachmentManagerButtonSubtext show={showAttachments}>{attachments.length}</AttachmentManagerButtonSubtext>
                        )
                    }
                </AttachmentManagerButton>
            </AttachmentManagerWrapper>

            <AttachmentBlockWrapper show={showAttachments}>
                <AttachmentListWrapper>
                    {
                        attachments.map((file) => {
                            const version = (file.versions && file.versions[0]) || null
                            if (!version) return null

                            // in view mode "&" is replaced with "&amp;"
                            const links = getFile(file, version)
                            const preview = links.preview.replace("&", "&amp;")
                            const download = links.download.replace("&", "&amp;")
                            const inText = description.indexOf(`href="${download}"`) > 0 || description.indexOf(`src="${preview}"`) > 0

                            return (
                                <AttachmentWrapper key={file.id} inText={inText}>
                                    <AttachmentLogo
                                        backgroundSrc={getFileLogo(file, version)}
                                        fileType={version.type}
                                    />
                                    <AttachmentTitle>
                                        {createFileName(file)}
                                    </AttachmentTitle>

                                    <DeleteAttachment
                                        onMouseEnter={defineTooltipAlign}
                                        onClick={() => deleteAttachment(file)}
                                    >
                                        <CrossIcon />
                                        <Tooltip align={tooltipAlign}>{lang.deleteAttachment}</Tooltip>
                                    </DeleteAttachment>

                                    <AddToTextAttachment
                                        inText={inText}
                                        onMouseEnter={defineTooltipAlign}
                                        onClick={() => {
                                            if (!inText) insertIntoText(file)
                                            if (inText) deleteFromText(file)
                                        }}
                                    >
                                        { !inText && <AddToTextIcon /> }
                                        { inText && <AddedToTextIcon /> }

                                        <Tooltip align={tooltipAlign}>{inText ? lang.removeFromText : lang.insertIntoText}</Tooltip>
                                    </AddToTextAttachment>
                                </AttachmentWrapper>
                            )
                        })
                    }
                </AttachmentListWrapper>

                <Col xs={12}>
                    <Button row>
                        <input type="file" onChange={uploadFile} />
                        {lang.upload}

                        { defaultStorage === STORAGE_AWS && <AmazonIcon /> }
                        { defaultStorage === STORAGE_GOOGLE && <GoogleIcon /> }
                        { defaultStorage === STORAGE_MICROSOFT && <OneDriveIcon /> }
                    </Button>
                    <Button
                        row
                        onClick={() => {
                            dispatch(addModal({
                                key: "drive",
                                content: <Drive onSelectFiles={addAttachments} />,
                            }))
                        }}
                    >
                        {lang.myDrive}
                    </Button>
                </Col>
            </AttachmentBlockWrapper>
        </FormGroup>
    )
}
